import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7890/api';

// Instancia de axios configurada
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para agregar token automáticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==========================================
// CALENDARIO (SISTEMA ORIGINAL)
// ==========================================

export const getLecturas = async (params = {}) => {
  try {
    const response = await api.get('/lecturas', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener lecturas:', error);
    throw error;
  }
};

export const getCitas = async (params = {}) => {
  try {
    const response = await api.get('/citas', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener citas:', error);
    throw error;
  }
};

export const getEventos = async (params = {}) => {
  try {
    const response = await api.get('/eventos', { params });
    return response.data;
  } catch (error) {
    console.error('Error al obtener eventos:', error);
    throw error;
  }
};

// ==========================================
// POSTS (BLOG)
// ==========================================

export const postsAPI = {
  // Listar posts publicados (público)
  getPublicados: (params = {}) => 
    api.get('/posts', { params }),

  // Obtener post por slug (público)
  getPorSlug: (slug) => 
    api.get(`/posts/${slug}`),

  // Obtener post por ID (admin)
  getPorId: (id) => 
    api.get(`/posts/admin/${id}`),

  // Crear post (admin)
  crear: (data) => 
    api.post('/posts', data),

  // Enviar post a revisión (usuario registrado)
  submitPost: (data) =>
    api.post('/posts/submit', data),

  // Actualizar post (admin)
  actualizar: (id, data) => 
    api.put(`/posts/${id}`, data),

  // Eliminar post (admin)
  eliminar: (id) => 
    api.delete(`/posts/${id}`),

  // Incrementar visualizaciones
  visualizar: (id) => 
    api.post(`/posts/${id}/visualizar`)
};

// ==========================================
// CATEGORÍAS (BLOG)
// ==========================================

export const categoriasAPI = {
  // Listar todas las categorías
  listar: () => 
    api.get('/categorias')
};

// ==========================================
// CATÁLOGOS (HISTORICIDAD)
// ==========================================

export const catalogosAPI = {
  // Listar catálogos
  listar: () => 
    api.get('/historicidad/catalogos'),

  // Obtener por slug
  getPorSlug: (slug) => 
    api.get(`/historicidad/catalogos/${slug}`)
};

// ==========================================
// DATOS RECOPILADOS (HISTORICIDAD)
// ==========================================

export const datosAPI = {
  // Listar datos publicados (público)
  getPublicados: (params = {}) => 
    api.get('/historicidad/publico', { params }),

  // Obtener por ID (público)
  getPorId: (id) => 
    api.get(`/historicidad/publico/${id}`),

  // Obtener por slug (público) - alias
  getPorSlug: (slug) => 
    api.get(`/historicidad/publico/${slug}`),

  // Incrementar visualizaciones
  visualizar: (id) => 
    api.post(`/historicidad/datos/${id}/visualizar`),

  // Obtener mi bandeja (privado)
  getBandeja: (params = {}) => 
    api.get('/historicidad/mi-bandeja', { params }),

  // Obtener dato completo (privado)
  getCompleto: (id) => 
    api.get(`/historicidad/datos/${id}`),

  // Obtener historial general con filtros (admin/gestor/validador/editor)
  obtenerHistorialGeneral: (params = {}) =>
    api.get('/historicidad/datos', { params }),

  // Crear dato (gestor)
  crear: (data) => 
    api.post('/historicidad/datos', data),

  // Enviar dato a revisión (usuario registrado)
  submitDato: (data) =>
    api.post('/historicidad/submit', data),

  // Actualizar dato
  actualizar: (id, data) => 
    api.put(`/historicidad/datos/${id}`, data),

  // Eliminar dato (admin)
  eliminar: (id) => 
    api.delete(`/historicidad/datos/${id}`)
};

// ==========================================
// FLUJO DE TRABAJO (HISTORICIDAD)
// ==========================================

export const flujoAPI = {
  // Transicionar estado
  transicionar: (datoId, estadoDestino, motivoTransicion) => 
    api.post(`/historicidad/datos/${datoId}/transicion`, { estadoDestino, motivoTransicion }),

  // Agregar retroalimentación
  agregarRetroalimentacion: (datoId, data) => 
    api.post(`/historicidad/datos/${datoId}/retroalimentacion`, data),

  // Responder retroalimentación
  responderRetroalimentacion: (id, respuesta) => 
    api.put(`/historicidad/retroalimentacion/${id}`, { respuesta }),

  // Obtener historial
  getHistorial: (datoId) => 
    api.get(`/historicidad/historial/${datoId}`),

  // Obtener retroalimentaciones
  getRetroalimentaciones: (datoId) => 
    api.get(`/historicidad/retroalimentaciones/${datoId}`),

  // Obtener transiciones
  getTransiciones: (datoId) => 
    api.get(`/historicidad/transiciones/${datoId}`),

  // Acción masiva
  accionMasiva: (datosIds, estadoDestino, motivoGeneral) => 
    api.post('/historicidad/accion-masiva', { datosIds, estadoDestino, motivoGeneral }),

  // Obtener configuración
  getConfiguracion: () => 
    api.get('/historicidad/configuracion-flujo'),

  // Actualizar configuración (Admin) - Endpoint antiguo
  actualizarConfiguracionHistoricidad: (data) =>
    api.put('/historicidad/configuracion-flujo', data),
    
  // Obtener estadísticas
  getEstadisticas: () =>
    api.get('/historicidad/estadisticas'),

  // Configuración del flujo - Nuevos endpoints
  listarConfiguraciones: () => 
    api.get('/flujos-config/configuraciones'),

  obtenerConfiguracionPorId: (id) => 
    api.get(`/flujos-config/configuraciones/${id}`),

  actualizarConfiguracion: (id, data) => 
    api.put(`/flujos-config/configuraciones/${id}`, data)
};

export default api;
